import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ISessionData, ISessionMessage } from "../../types/session.type";
import type { SessionType } from "../../types/session.type";
import type { ISessionFormApiPayload } from "../../types/form.type";

// Черновик формы кампании, присланный сервером через WS campaign_form.update
export interface CampaignFormUpdate {
	sessionId: string;
	payload: ISessionFormApiPayload;
	/** Порядковый номер черновика в store: Form.tsx применяет каждый ровно один раз. */
	seq: number;
}

interface SessionState {
	isLoading: boolean;
	sessionId: string | null;
	/** Черновик «нового чата» без session_id (ba-drafts). */
	activeDraftId: string | null;
	activeChatKind: SessionType | null;
	sessionData: ISessionData | null;
	freezeUI: boolean;
	sessionProgress: number;
	/** Обраний Google Ads аккаунт у чаті (header Dialog + форма кампанії). */
	activeAdAccountId: string;
	/** Последний черновик формы из WS campaign_form.update (basic-режим). */
	campaignFormUpdate: CampaignFormUpdate | null;
	/** seq черновика, уже применённого к форме (см. Form.tsx). */
	appliedCampaignFormUpdateSeq: number | null;
}

const initialState: SessionState = {
	isLoading: true, // Состояние загрузки
	sessionId: null, // Id для упревлениея переключением сессиями
	activeDraftId: null,
	activeChatKind: null,
	sessionData: null, // Данные сессии (вместе с сообщениями)
	freezeUI: false, // true - Заморозить UI
	sessionProgress: 0, // Прогрес загрузки сессии
	activeAdAccountId: "",
	campaignFormUpdate: null,
	appliedCampaignFormUpdateSeq: null,
};

export const sessionSlice = createSlice({
	name: "session",
	initialState,
	reducers: {
		setSessionId(state, action: PayloadAction<string | null>) {
			if (state.sessionId === action.payload) return;
			state.sessionId = action.payload;
			if (action.payload !== null) state.activeDraftId = null;
			if (action.payload === null) state.activeChatKind = null;
		},
		setActiveChatKind(state, action: PayloadAction<SessionType | null>) {
			state.activeChatKind = action.payload;
		},
		setActiveDraftId(state, action: PayloadAction<string | null>) {
			if (state.activeDraftId === action.payload) return;
			state.activeDraftId = action.payload;
			if (action.payload !== null) state.sessionId = null;
		},
		setSessionData(state, action: PayloadAction<ISessionData | null>) {
			if (action.payload !== null) state.isLoading = false;
			state.sessionData = action.payload;
		},
		setIsLoading(state, action: PayloadAction<boolean>) {
			state.isLoading = action.payload;
		},
		setFreezeUI(state, action: PayloadAction<boolean>) {
			state.freezeUI = action.payload;
		},
		setSessionProgress(state, action: PayloadAction<number>) {
			state.sessionProgress = action.payload;
		},
		appendMessage(state, action: PayloadAction<ISessionMessage>) {
			if (state.sessionData !== null) {
				state.sessionData.messages.push(action.payload);
				state.sessionData.count += 1;
			}
		},
		clearMessages(state) {
			if (state.sessionData !== null) {
				state.sessionData.messages = [];
				state.sessionData.count = 0;
			}
		},
		setActiveAdAccountId(state, action: PayloadAction<string>) {
			state.activeAdAccountId = action.payload;
		},
		// Черновик формы кампании, сгенерированный сервером (WS campaign_form.update)
		setCampaignFormUpdate(
			state,
			action: PayloadAction<Omit<CampaignFormUpdate, "seq">>,
		) {
			// seq растёт монотонно: повторный черновик с тем же содержимым считается новым
			const seq = (state.campaignFormUpdate?.seq ?? 0) + 1;
			state.campaignFormUpdate = { ...action.payload, seq };
		},
		// Помечает черновик применённым: Form.tsx применяет каждый ровно один раз,
		// иначе ремоунт формы или возврат в сессию затирали бы ручные правки.
		// Сам campaignFormUpdate остаётся в store: он нужен guard'у гидратации.
		markCampaignFormUpdateApplied(state, action: PayloadAction<number>) {
			state.appliedCampaignFormUpdateSeq = action.payload;
		},
	},
});

export const {
	setSessionId,
	setActiveDraftId,
	setActiveChatKind,
	setSessionData,
	setIsLoading,
	setFreezeUI,
	setSessionProgress,
	appendMessage,
	clearMessages,
	setActiveAdAccountId,
	setCampaignFormUpdate,
	markCampaignFormUpdateApplied,
} = sessionSlice.actions;

export default sessionSlice.reducer;
